import { Box } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

const OCR_SCAN_ANIMATION_SRC = '/animations/scan-document.lottie';
const CANVAS_WIDTH = 70;
const CANVAS_HEIGHT = 80;

export const OCR_SCAN_ANIMATION_TEST_ID = 'ocr-scan-animation';
export const OCR_SCAN_ANIMATION_FALLBACK_TEST_ID = 'ocr-scan-animation-fallback';

const ScanDocumentFallback = () => (
  <Box
    aria-hidden="true"
    data-testid={OCR_SCAN_ANIMATION_FALLBACK_TEST_ID}
    h={`${CANVAS_HEIGHT}px`}
    position="relative"
    w={`${CANVAS_WIDTH}px`}
  >
    <Box
      border="2px solid"
      borderColor="#005BBB"
      borderRadius="4px"
      h="64px"
      left="10px"
      position="absolute"
      top="8px"
      w="50px"
    />
    <Box
      bg="#005BBB"
      borderRadius="1px"
      h="3px"
      left="6px"
      opacity={0.85}
      position="absolute"
      sx={{
        '@keyframes ocrScanSweep': {
          '0%': { top: '12px' },
          '50%': { top: '56px' },
          '100%': { top: '12px' },
        },
        animation: 'ocrScanSweep 1.6s ease-in-out infinite',
      }}
      w="58px"
    />
  </Box>
);

export const OcrScanAnimation = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || showFallback) return undefined;

    let isDisposed = false;
    let dotLottie: { destroy: () => void } | null = null;
    let rafId = 0;

    const waitForLayout = () =>
      new Promise<void>((resolve) => {
        rafId = window.requestAnimationFrame(() => {
          rafId = window.requestAnimationFrame(() => resolve());
        });
      });

    const loadAnimation = async () => {
      try {
        await waitForLayout();
        if (isDisposed) return;

        if (canvas.clientWidth <= 0 || canvas.clientHeight <= 0) {
          setShowFallback(true);
          return;
        }

        const { DotLottie } = await import('@lottiefiles/dotlottie-web');
        if (isDisposed) return;

        dotLottie = new DotLottie({
          autoplay: true,
          canvas,
          layout: {
            align: [0.5, 0.5],
            fit: 'contain',
          },
          loop: true,
          renderConfig: {
            autoResize: false,
            devicePixelRatio: 1,
          },
          src: OCR_SCAN_ANIMATION_SRC,
        });
      } catch {
        if (!isDisposed) {
          setShowFallback(true);
        }
      }
    };

    void loadAnimation();

    return () => {
      isDisposed = true;
      window.cancelAnimationFrame(rafId);
      try {
        dotLottie?.destroy();
      } catch {
        // DotLottie may throw during teardown after a WASM render error.
      }
    };
  }, [showFallback]);

  if (showFallback) {
    return <ScanDocumentFallback />;
  }

  return (
    <Box
      aria-hidden="true"
      bg="white"
      h={`${CANVAS_HEIGHT}px`}
      w={`${CANVAS_WIDTH}px`}
    >
      <canvas
        data-testid={OCR_SCAN_ANIMATION_TEST_ID}
        height={CANVAS_HEIGHT}
        ref={canvasRef}
        style={{ display: 'block', height: '100%', width: '100%' }}
        width={CANVAS_WIDTH}
      />
    </Box>
  );
};
